import { useInfiniteQuery } from '@zg-rentals/react-query5';
import {
  RentalManagerAccountViewZramMultifamilyWebApiHandler_list,
  RentalManagerAccountViewZramSingleFamilyWebApiHandler_list,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_listingQualityScore,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_options,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_prospects,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_sources,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_status,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_advertising,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_prospects,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_sources,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_status,
} from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../selectors/useSelector';
import PropertiesConstants from '../../constants/PropertiesConstants';

import type { AccountViewListingsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../containers/ZRAMPropertiesPage';

export const transformFiltersToApiParams = (filters: FilterState) => ({
  includeActive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeInactive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeArchived: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeUnarchived: filters.subNavFilterType !== PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeFreeClaimedListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED,
  includeFreeBonusListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  includeAtRiskOnly: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  searchText: filters.searchText,
  limit: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT,
  startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
});

export const useGetPropertiesData = (filters: FilterState) => {
  const user = useSelector((state) => state.user);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } = useInfiniteQuery<
    AccountViewListingsDto,
    Error,
    { pages: Array<AccountViewListingsDto> },
    Array<string | FilterState>,
    string
  >({
    queryKey: ['getProperties', filters],
    queryFn: ({ pageParam }) =>
      user.isSingleFamilyEnterpriseCustomer
        ? RentalManagerAccountViewZramSingleFamilyWebApiHandler_list({
            ...transformFiltersToApiParams(filters),
            startKeyExclusive: pageParam,
          })
        : RentalManagerAccountViewZramMultifamilyWebApiHandler_list({
            ...transformFiltersToApiParams(filters),
            startKeyExclusive: pageParam,
          }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
    // @ts-expect-error - returning undefined as it would be during pending
    placeholderData: (previousData) => {
      if (!previousData?.pages?.[0]?.listingCount) {
        return;
      }

      return {
        pages: [
          {
            listingCount: previousData.pages[0].listingCount,
          },
        ],
        pageParams: previousData.pageParams,
      };
    },
  });

  const propertiesData = data?.pages.length
    ? {
        listings: data.pages.flatMap((page) => page.listings),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useZramStatus = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const user = useSelector((state) => state.user);

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_status(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user.isSingleFamilyEnterpriseCustomer
        : !user.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_status(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user.isSingleFamilyEnterpriseCustomer
        : user.isSingleFamilyEnterpriseCustomer,
  });

  return user.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramListingQualityScore = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const user = useSelector((state) => state.user);

  // Skip listing quality score completely for single family enterprise users
  return useRentalManagerAccountViewZramMultifamilyWebApiHandler_listingQualityScore(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user.isSingleFamilyEnterpriseCustomer
        : !user.isSingleFamilyEnterpriseCustomer,
  });
};

export const useZramProspects = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const user = useSelector((state) => state.user);

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_prospects(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user.isSingleFamilyEnterpriseCustomer
        : !user.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_prospects(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user.isSingleFamilyEnterpriseCustomer
        : user.isSingleFamilyEnterpriseCustomer,
  });

  return user.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramAdvertising = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const user = useSelector((state) => state.user);

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user.isSingleFamilyEnterpriseCustomer
        : !user.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_advertising(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user.isSingleFamilyEnterpriseCustomer
        : user.isSingleFamilyEnterpriseCustomer,
  });

  return user.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramSources = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const user = useSelector((state) => state.user);

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_sources(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user.isSingleFamilyEnterpriseCustomer
        : !user.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_sources(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user.isSingleFamilyEnterpriseCustomer
        : user.isSingleFamilyEnterpriseCustomer,
  });

  return user.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramOptions = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const user = useSelector((state) => state.user);

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_options(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user.isSingleFamilyEnterpriseCustomer
        : !user.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user.isSingleFamilyEnterpriseCustomer
        : user.isSingleFamilyEnterpriseCustomer,
  });

  return user.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};
